import {
  DISPOSITION_DEFINITIONS,
  GC_STATUS_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_STAGE_DEFINITIONS,
  PROJECT_STAGE_VALUES,
  STORIES_KNOWN_DEFINITIONS,
  PRIMARY_USE_VALUES as TYPES_PRIMARY_USE_VALUES,
} from '@parcel/types';
import type { EntityFilterSchema, FilterEnumValue } from '../types.js';

export { PROJECT_STAGE_VALUES };

/** Disposition filter options, labels/descriptions from the canonical @parcel/types definitions. */
export const DISPOSITION_OPTIONS: readonly FilterEnumValue[] = DISPOSITION_DEFINITIONS.map((d) => ({
  value: d.value,
  label: d.label,
  description: d.description,
}));

// Labels + descriptions come from the canonical @parcel/types definitions so a
// Stage badge tooltip and the Stage filter read identically (single vocabulary).
export const PROJECT_STAGE_OPTIONS: readonly FilterEnumValue[] = PROJECT_STAGE_DEFINITIONS.map(
  (d) => ({ value: d.value, label: d.label, description: d.description }),
);

export const PRIMARY_USE_VALUES = TYPES_PRIMARY_USE_VALUES;
export const PRIMARY_USE_OPTIONS: readonly FilterEnumValue[] = PRIMARY_USE_DEFINITIONS;

/** Read only by the old score criterion kinds until Filters PR2; tables filter with where. */
export const projectFilters: EntityFilterSchema = {
  entity: 'project',
  fields: [
    {
      type: 'text',
      key: 'q',
      label: 'Search',
      description: 'Free-text search over project name and primary address (trigram similarity).',
    },
    {
      type: 'enum',
      key: 'stage',
      label: 'Stage',
      description: 'Canonical development stage of the project.',
      multi: true,
      values: PROJECT_STAGE_OPTIONS,
    },
    {
      type: 'enum',
      key: 'disposition',
      label: 'Disposition',
      description: 'Whether the project is active or reached a terminal outcome.',
      multi: true,
      values: DISPOSITION_OPTIONS,
    },
    {
      type: 'enum',
      key: 'primary_use',
      label: 'Type',
      description: 'Primary use / asset type of the project.',
      multi: true,
      values: PRIMARY_USE_OPTIONS,
    },
    {
      type: 'dynamic-enum',
      key: 'city',
      label: 'City',
      description: 'City the project is located in. Values come from workspace settings.',
      multi: true,
      valueSource: 'workspace-cities',
    },
    {
      type: 'dynamic-enum',
      key: 'neighborhood',
      label: 'Neighborhood',
      description:
        'Neighborhood the project is located in, as the city names it. Boston only today; projects elsewhere have none.',
      multi: true,
      valueSource: 'workspace-neighborhoods',
    },
    {
      type: 'number-range',
      key: 'sqft',
      label: 'Square footage',
      description: 'Gross floor area of the project in square feet.',
      unit: 'sq ft',
    },
    {
      type: 'number-range',
      key: 'units',
      label: 'Units',
      description: 'Number of residential units in the project.',
      unit: 'units',
    },
    {
      type: 'number-range',
      key: 'stories',
      label: 'Stories',
      description:
        'Number of storeys a filing or the project site states. A range never matches a project whose storey count is unknown; use Stories known to find those.',
      unit: 'stories',
    },
    {
      type: 'enum',
      key: 'stories_known',
      label: 'Stories known',
      description: 'Whether any source states the number of storeys.',
      multi: false,
      values: STORIES_KNOWN_DEFINITIONS,
    },
    {
      type: 'number-range',
      key: 'cost',
      label: 'Construction cost',
      description:
        'Stated construction cost in US dollars (cost_of_construction_usd). A range never matches a project whose cost is unknown.',
      unit: 'USD',
    },
    {
      type: 'enum',
      key: 'gc_status',
      label: 'General contractor',
      description:
        'Whether a general contractor is named, stated as not yet selected, or unknown. Derived at read time.',
      multi: true,
      values: GC_STATUS_DEFINITIONS,
    },
    {
      type: 'date-range',
      key: 'detected',
      label: 'Detected',
      description:
        'Date of the most recent signal on the project (falls back to the first signal).',
    },
  ],
};
